import { Inject, Injectable, Optional } from '@angular/core';
import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client/core';
import { CraftQueries } from '@core-mkt/craft/craft-queries';
import type { CraftQuery } from '@core-mkt/craft/craft-query';
import type { CraftSiteTemplateMapping } from '@core-mkt/craft/craft-site-template-mapping';
import type { CraftTemplateType } from '@core-mkt/craft/craft-template-types';
import { SingleEntryQuery } from '@core-mkt/craft/single-entry-query';
import { environment } from '@core-mkt/environments/environment';
import gql from 'graphql-tag';
import fetch from 'node-fetch';
import { TemplateSingleQuery } from '../../../shared/templates/single-template/template-component/template-single-query';
import { BrandConfigurationService } from '../configuration/brand-configuration.service';

@Injectable({
  providedIn: 'root',
})
export class CraftQueryService {
  craftApiUrl: string;
  blogCraftApiUrl: string;
  blogPrefix: string;
  cache: InMemoryCache;

  constructor(
    @Inject(CraftQueries) @Optional() private craftQueries: CraftSiteTemplateMapping[],
    private bcs: BrandConfigurationService,
  ) {
    this.craftQueries = CraftQueries;

    this.craftApiUrl = environment.craftApiUrl;
    this.blogCraftApiUrl = environment.blogCraftApiUrl;
    this.blogPrefix = environment.blogPrefix;

    this.cache = new InMemoryCache();
  }

  /**
   * createClient creates a new Apollo client for each request to avoid singleton state mutation.
   * This prevents race conditions in SSR where concurrent requests could contaminate each other's state.
   *
   * @param {string} apiUrl - The Craft API URL to use (regular or blog)
   * @param {string} [token] - Optional preview token to append to the API URL
   * @return A new Apollo client configured for this request
   *
   */
  private createClient(apiUrl: string, token?: string) {
    const uri = token ? `${apiUrl}?token=${token}` : apiUrl;
    const httpLink = createHttpLink({
      uri,
      // Use window.fetch (which Sentry instruments) instead of the imported node-fetch
      fetch: (typeof window !== 'undefined' ? window.fetch.bind(window) : fetch) as any,
      // Request uncompressed responses. On the server, node-fetch otherwise advertises
      // gzip/br and decompresses the reply — a step the Angular 21 SSR bundle mishandles,
      // surfacing as node-fetch "Premature close" on gqlproxy's compressed responses
      // (direct-to-Craft, which is uncompressed, is unaffected). In the browser,
      // Accept-Encoding is a forbidden header and is ignored, so this is a safe no-op.
      headers: { 'accept-encoding': 'identity' },
    });
    return new ApolloClient({
      link: httpLink,
      cache: this.cache,
    });
  }

  /**
   * pathToSlug takes the forward slashes, "/", in a string and converts them to double dashes, "--".
   * texas/drivers-ed/adult = texas--drivers-ed--adult.
   *
   * @param {string} path - The path to convert to a slug
   * @return {string} slug
   *
   */
  public pathToSlug(path: string): string {
    return path.replace(/\//g, '--');
  }

  /**
   * flattenCkeditorFields recursively converts Craft 5 CKEditor field objects ({ html: '...' })
   * back into plain HTML strings. In Craft 4 (Redactor) these fields were returned as strings;
   * Craft 5's CKEditor fields are object types requiring an { html } sub-selection. Flattening here
   * keeps every downstream component working with the original string shape.
   *
   * @param {unknown} value - Any node of the Craft query result data
   * @return {unknown} The same structure with CKEditor field objects replaced by their html strings
   *
   */
  public flattenCkeditorFields(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.flattenCkeditorFields(item));
    }
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const keys = Object.keys(obj);
      const isCkeditorField = typeof obj.html === 'string' && keys.every((k) => k === 'html' || k === '__typename');
      if (isCkeditorField) {
        return obj.html;
      }
      const flattened: Record<string, unknown> = {};
      for (const key of keys) {
        flattened[key] = this.flattenCkeditorFields(obj[key]);
      }
      return flattened;
    }
    return value;
  }

  /**
   * lookupQuery takes in a CraftTemplateType and returns the corresponding query.
   *
   * @param {CraftTemplateType} craftTemplateType - a supported craft template type
   * @returns {CraftQuery}
   *
   */
  public lookupQuery(
    craftTemplateType: CraftTemplateType,
    craftQueries: CraftSiteTemplateMapping[] = this.craftQueries,
  ): CraftQuery {
    const queryMapping = craftQueries.filter((obj) => {
      return obj.site == this.bcs.id;
    });
    const query = queryMapping[0].queries.get(craftTemplateType);
    if (query) {
      return query;
    } else {
      return null;
    }
  }

  /**
   * getQuery takes a path, a token, and an optional slug and makes a call to crafts graphQL API to get an entry's page data from the slug / path.
   * The option to pass in a slug instead of always deriving it from the path is in the event that this function is being used for a page preview.
   *
   * @param {string} path - The path of the current page being rendered
   * @param {string} token - The craft preview token
   * @param {string} site - The lowercase brand ID
   * @param {string} [slug] - An optional slug in the case of a preview render where the path is not supplied.
   * @param {boolean} isBlog - Whether the query is for a blog post
   * @return {Promise<any>} The page's craft data
   *
   */
  public async getQuery(path: string, token: string, site: string, slug?: string, isBlog?: boolean): Promise<any> {
    if (!slug) {
      slug = this.pathToSlug(path);
    }

    // If this is a blog query and we have a blogPrefix, ensure the slug has the prefix
    if (isBlog && this.blogPrefix && !slug.startsWith(this.blogPrefix)) {
      slug = this.blogPrefix + slug;
    }

    // Determine which API URL to use based on isBlog flag
    const apiUrl = isBlog ? this.blogCraftApiUrl : this.craftApiUrl;

    // Create a new Apollo client for this request to avoid singleton state mutation
    const apolloClient = this.createClient(apiUrl, token);

    // Query Craft using the single vision template query
    const result = await apolloClient.query({
      fetchPolicy: 'no-cache', // Let's us do 'fragmenting' without definition. This is fine because Craft defines its own structure.
      query: gql`
        ${TemplateSingleQuery.query}
      `,
      variables: {
        site: site,
        slug: slug,
      },
    });

    return { ...result, data: this.flattenCkeditorFields(result.data) };
  }

  /**
   * queryCraftLookup takes a path, a token, and an optional slug and makes a call to crafts graphQL API to get an entry's template data from the slug / path.
   * The option to pass in a slug instaed of always deriving it from the path is in the event that this function is being used for a page preview.
   *
   * @param {string} path - The path of the current page being rendered
   * @param {string} token - The craft preview token
   * @param {string} site - The lowercase brand ID
   * @param {string} [slug] - An optional slug in the case of a preview render where the path is not supplied.
   * @return {Promise<any>} The page's craft data
   *
   */
  public async queryCraftLookup(
    path: string,
    token: string,
    site: string,
    slug?: string,
    isBlog?: boolean,
  ): Promise<any> {
    if (!slug) {
      slug = this.pathToSlug(path);
    }

    // If this is a blog query and we have a blogPrefix, ensure the slug has the prefix
    if (isBlog && this.blogPrefix) {
      // Check if the slug already contains the prefix
      if (!slug.startsWith(this.blogPrefix)) {
        slug = this.blogPrefix + slug;
      }
    }

    // Determine which API URL to use based on isBlog flag
    const apiUrl = isBlog ? this.blogCraftApiUrl : this.craftApiUrl;

    // Create a new Apollo client for this request to avoid singleton state mutation
    const apolloClient = this.createClient(apiUrl, token);

    // Query Craft
    const result = await apolloClient.query({
      fetchPolicy: 'no-cache', // Let's us do 'fragmenting' without definition. This is fine because Craft defines its own structure.
      query: gql`
        ${SingleEntryQuery.query}
      `,
      variables: {
        site: site,
        slug: slug,
      },
    });
    return { ...result, data: this.flattenCkeditorFields(result.data) };
  }
}
